/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

// Component imports
import { Grid, Layout } from 'antd';
import GlobalFooter from './GlobalFooter';
import GlobalHeader from './GlobalHeader';
import GlobalSider from './GlobalSider';
import MobileNavBar from './MobileNavBar';
import HRWizHeader from './HRWizHeader';
import MyProfileFormModal from '../Modals/MyProfileFormModal';

// Misc imports
import is from '../../utils/is';
import { updateRecentBots } from '../../store/bots/botsActions';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { TEMP_STORAGE_KEYS } from '../../constants/main';
import { IS_HRWIZ_CONSOLE_ENABLED, IS_LIVECHAT_CONSOLE_ONLY } from '../../constants/env';

// Constant declarations
const { Content } = Layout;
const { useBreakpoint } = Grid;

const MainLayout = props => {
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  // Fetch the bots for the current organization in a generalized place,
  // so that we can use them in various places with getCurrentQueryState
  useFetchBotsQuery({ orgId }, { skip: !orgId });

  // Edge-case for when someone navigates to a new organization by adding the URL directly
  // Make sure to clean up the recent bots list because these bots do not belong in the current org
  useEffect(() => {
    const storedOrgId = window.localStorage.getItem(TEMP_STORAGE_KEYS.ORGANIZATION_ID);

    if (orgId && orgId !== storedOrgId) {
      dispatch(updateRecentBots([]));
      window.localStorage.setItem(TEMP_STORAGE_KEYS.ORGANIZATION_ID, orgId);
    }
  }, [orgId, dispatch]);

  const renderHeader = () => {
    if (is.inMicrosoftTeams()) {
      return;
    }

    if (IS_HRWIZ_CONSOLE_ENABLED) {
      return is.hrwizHeaderVisible() ? <HRWizHeader /> : null;
    }

    // Else, if we are not in HRWiz console and not in MS Teams, we should render the GlobalHeader
    return <GlobalHeader />;
  };

  return (
    <Fragment>
      {renderHeader()}

      <Layout className="main-layout">
        {IS_HRWIZ_CONSOLE_ENABLED ? null : <GlobalSider />}

        <Content>
          <div
            className="content-children d-flex flex-column justify-content-between"
            style={{
              ...IS_LIVECHAT_CONSOLE_ONLY || is.inMicrosoftTeams() ? {
                paddingLeft: isMobileOrTabletSize ? 0 : 65,
                minHeight: '100vh'
              } : {}
            }}
          >
            <div className="flex-fill">
              {props.children}
            </div>

            <div>
              <GlobalFooter />
            </div>
          </div>
        </Content>
      </Layout>

      {isMobileOrTabletSize && !IS_HRWIZ_CONSOLE_ENABLED && !is.inMicrosoftTeams() ? <MobileNavBar /> : null}

      {/* My Profile form modal - open and close controlled by the component itself */}
      <MyProfileFormModal />
    </Fragment>
  );
};

MainLayout.propTypes = {
  children: PropTypes.node
};

export default MainLayout;
